// BusinessCalendar.jsx (Updated with SelectionDialog)
import React, { useEffect, useState, useRef } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { showBookings } from '../../services/api';
import BookingPopup from '../Popup/PopupBooking';
import SelectionDialog from '../SelectionDialog/SelectionDialog'; // Import the new component
import "./BusinessCalendar.scss"
import { toast } from 'react-toastify';

// Optional: Create a custom confirmation dialog component
const ConfirmationDialog = ({ isOpen, title, message, onConfirm, onCancel }) => {
  if (!isOpen) return null;

  return (
    <div className="confirmation-dialog-overlay">
      <div className="confirmation-dialog">
        <h3 className="dialog-title">{title}</h3>
        <p className="dialog-message">{message}</p>
        <div className="dialog-buttons">
          <button onClick={onCancel} className="btn-cancel">
            Cancel
          </button>
          <button onClick={onConfirm} className="btn-confirm">
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

const BusinessCalendar = ({ clientId, calendarId, colorData }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const refreshInterval = useRef(null);

  // New state for popup
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  // State for confirmation dialogs
  const [showRescheduleConfirm, setShowRescheduleConfirm] = useState(false);
  const [showDurationConfirm, setShowDurationConfirm] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  // New state for selection dialog
  const [showSelectionDialog, setShowSelectionDialog] = useState(false);
  const [selectedRange, setSelectedRange] = useState(null);
  const [selectionInfo, setSelectionInfo] = useState(null);

  // Status color mapping
  const statusColors = {
    scheduled: '#3788d8',
    confirmed: '#10b981',
    cancelled: '#ef4444',
    completed: '#8b5cf6',
    'no-show': '#f59e0b',
  };



  // Main fetch function
  const fetchBookings = async () => {
    try {
      setError(null);

      const bookings = await showBookings(clientId, calendarId);

      const now = new Date();
      const upcoming = bookings.filter(b =>
        new Date(b.start_time) >= now
      );

      const formattedEvents = upcoming.map(booking => {
        const isBlocked = booking.customer_name === "UNAVAILABLE";

        return {
          id: booking.id.toString(),
          title: isBlocked
            ? "🚫 UNAVAILABLE"
            : `${booking.customer_name} (${booking.status})`,
          start: booking.start_time,
          end: booking.end_time,
          backgroundColor: isBlocked
            ? '#d1d5db'
            : (statusColors[booking.status] || '#6b7280'),
          borderColor: isBlocked ? '#9ca3af' : 'transparent',
          textColor: isBlocked ? '#4b5563' : '#ffffff',
          editable: !isBlocked,
          extendedProps: {
            customerName: booking.customer_name,
            status: booking.status,
            booking_notes: booking.booking_notes,
            my_notes: booking.my_notes,
            customerEmail: booking.customer_email,
            customerPhone: booking.customer_phone,
            createdAt: booking.created_at,

          },
        };
      });

      setEvents(prev => {
        // First load
        if (prev.length === 0) {
          setLastRefresh(new Date());
          setLoading(false);
          return formattedEvents;
        }

        const prevIds = new Set(prev.map(e => e.id));
        const newIds = new Set(formattedEvents.map(e => e.id));

        // ✅ NEW bookings (exist in server but not in state)
        const newEvents = formattedEvents.filter(e => !prevIds.has(e.id));

        // ✅ DELETED bookings (exist in state but not in server)
        const deletedIds = prev
          .filter(e => !newIds.has(e.id))
          .map(e => e.id);

        const hasNew = newEvents.length > 0;
        const hasDeleted = deletedIds.length > 0;

        // 🚫 No changes → no rerender
        if (!hasNew && !hasDeleted) {
          setLastRefresh(new Date());
          setLoading(false);
          return prev;
        }

        // Remove deleted ones
        const cleanedPrev = prev.filter(e => !deletedIds.includes(e.id));

        setLastRefresh(new Date());
        setLoading(false);

        // Add new ones
        return [...cleanedPrev, ...newEvents];
      });


    } catch (err) {
      console.error("Error loading calendar:", err);
      setError(err.message || 'Failed to load calendar data');
      setLoading(false);
    }
  };


  // Set up auto-refresh
  useEffect(() => {
    if (!clientId) return;

    fetchBookings();

    refreshInterval.current = setInterval(fetchBookings, 30000);

    return () => {
      if (refreshInterval.current) {
        clearInterval(refreshInterval.current);
      }
    };
  }, [clientId]);


  // Manual refresh function
  const manualRefresh = async () => {
    await fetchBookings();
  };

  // Handle date click
  const handleDateClick = (info) => {
    toast.info('New booking date or set unavailable?');
  };

  // Handle event click - Open popup instead of alert
  const handleEventClick = (info) => {

    const event = info.event;
    const createdAt = event.extendedProps.createdAt;
    console.log(event.extendedProps.booking_notes);
    setSelectedBooking({
      id: event.id,
      title: event.title,
      start: event.start,
      end: event.end,
      extendedProps: event.extendedProps,
      createdAt: event.extendedProps.createdAt,
      notes: event.extendedProps.booking_notes

    });
    setIsPopupOpen(true);
  };



  // Handle event drop (rescheduling)
  const handleEventDrop = async (info) => {
    // Store the event info for later use
    setPendingAction({ type: 'drop', info });

    // Show custom confirmation dialog
    if (window.confirm && window.confirm('Are you sure you want to reschedule this booking irfan?')) {
      await processReschedule(info);
    } else {
      // Fallback: Use a custom confirmation state
      setShowRescheduleConfirm(true);
    }
  };

  // Process rescheduling after confirmation
  const processReschedule = async (info) => {
    try {
      const event = info.event;
      const bookingId = parseInt(event.id);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/bookings/rescheduled/${bookingId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          start_time: event.start.toISOString(),
          end_time: event.end.toISOString(),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to reschedule booking');
      }

      // Show success notification
      toast.success('Booking rescheduled successfully!');
      await fetchBookings(); // Refresh after successful update

    } catch (err) {
      console.error('Error rescheduling booking:', err);
      // Revert the change
      info.revert();

      // Show error message
      alert('Failed to reschedule booking: ' + err.message);
    }
  };

  // Handle event resize (duration change)
  const handleEventResize = async (info) => {
    // Store the event info for later use
    setPendingAction({ type: 'resize', info });

    setShowDurationConfirm(true);
  };

  // Process resize after confirmation
  const processResize = async (info) => {
    try {
      const event = info.event;
      const bookingId = parseInt(event.id);

      const response = await fetch(`/api/bookings/${bookingId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          start_time: event.start.toISOString(),
          end_time: event.end.toISOString(),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update booking duration');
      }

      toast.success('Booking duration updated successfully!');
      await fetchBookings(); // Refresh after successful update

    } catch (err) {
      console.error('Error updating booking duration:', err);
      // Revert the change
      info.revert();

      // Show error message
      alert('Failed to update duration: ' + err.message);
    }
  };

  // Handle confirmations from custom dialogs
  const handleRescheduleConfirm = () => {
    if (pendingAction?.type === 'drop') {
      processReschedule(pendingAction.info);
    }
    setShowRescheduleConfirm(false);
    setPendingAction(null);
  };

  const handleRescheduleCancel = () => {
    if (pendingAction?.type === 'drop') {
      pendingAction.info.revert();
    }
    setShowRescheduleConfirm(false);
    setPendingAction(null);
  };

  const handleDurationConfirm = () => {
    if (pendingAction?.type === 'resize') {
      processResize(pendingAction.info);
    }
    setShowDurationConfirm(false);
    setPendingAction(null);
  };

  const handleDurationCancel = () => {
    if (pendingAction?.type === 'resize') {
      pendingAction.info.revert();
    }
    setShowDurationConfirm(false);
    setPendingAction(null);
  };

  // Function to block time slot
  const blockTimeSlot = async (start, end, durationMinutes) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/bookings/create`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            clientId: clientId,
            calendarId: calendarId,
            customerName: "UNAVAILABLE",
            appointmentTime: start.toISOString(),
            duration: durationMinutes,
            note: "Time slot manually blocked by staff",
            customerPhone: "",
            customerAddress: "",
            customerPostcode: "",
            status: "blocked"
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to block slots');
      }

      const data = await response.json();
      toast.success('Time blocked successfully as unavailable');
      //console.log('Blocked booking ID:', data.bookingId);
      await fetchBookings(); // Refresh the UI
    } catch (err) {
      console.error('Error blocking slots:', err);
      alert('Error blocking slots: ' + err.message);
    }
  };

  // Updated handleSelectRange function
  const handleSelectRange = (selectionInfo) => {
    const { start, end } = selectionInfo;

    // Store the selection info
    setSelectionInfo(selectionInfo);
    setSelectedRange({ start, end });
    setShowSelectionDialog(true);
  };

  // Function to handle create new booking option
  const handleCreateBooking = () => {
    const { start, end } = selectedRange;

    // Open the booking popup with the selected time range
    setSelectedBooking({
      id: null, // New booking
      title: "New Booking",
      start: start,
      end: end,
      extendedProps: {
        customerName: "",
        status: "scheduled",
        booking_notes: "",
        my_notes: "",
        customerEmail: "",
        customerPhone: ""
      }
    });
    setIsPopupOpen(true);
  };

  // Function to handle block booking option
  const handleBlockBooking = async () => {
    const { start, end } = selectedRange;
    const durationMinutes = Math.round((end - start) / (1000 * 60));

    await blockTimeSlot(start, end, durationMinutes);
  };

  // Function to handle cancel selection
  const handleCancelSelection = () => {
    // Clear the selection highlight on the calendar
    if (selectionInfo) {
      selectionInfo.view.calendar.unselect();
    }
  };

  const handleBookingStatusUpdate = async (bookingId, newStatus = null, updates = {}) => {
    try {
      setEvents(prevEvents =>
        prevEvents.map(event => {
          if (event.id === bookingId) {

            // Only update title/status if a real status is provided
            const shouldUpdateStatus =
              newStatus && ["scheduled", "confirmed", "cancelled", "completed", "no-show"].includes(newStatus);

            return {
              ...event,
              title: shouldUpdateStatus
                ? event.title.replace(/\(.*?\)/, `(${newStatus})`)
                : event.title,
              backgroundColor: shouldUpdateStatus
                ? statusColors[newStatus] || "#6b7280"
                : event.backgroundColor,
              extendedProps: {
                ...event.extendedProps,
                ...(shouldUpdateStatus ? { status: newStatus } : {}),
                ...updates, // merge notes or any other fields
              },
            };
          }
          return event;
        })
      );
    } catch (err) {
      console.error("Error updating local state:", err);
      fetchBookings();
    }
  };


  // Close popup
  const closePopup = () => {
    setIsPopupOpen(false);
    setSelectedBooking(null);
  };

  // Get status counts
  const getStatusCounts = () => {
    const counts = {
      scheduled: 0,
      confirmed: 0,
      cancelled: 0,
      completed: 0,
      'no-show': 0
    };

    events.forEach(event => {
      const status = event.extendedProps?.status;
      if (status && counts.hasOwnProperty(status)) {
        counts[status]++;
      }
    });

    return counts;
  };

  // Format time for display
  const formatTimeForDisplay = (start, end) => {
    if (!start || !end) return '';

    const startTime = start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const endTime = end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const durationMinutes = Math.round((end - start) / (1000 * 60));

    return `${startTime} to ${endTime} (${durationMinutes} minutes)`;
  };

  if (loading && !lastRefresh) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="text-lg text-gray-600">Loading calendar...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center p-8">
        <div className="text-red-500 text-lg mb-2">Error loading calendar</div>
        <div className="text-gray-600 mb-4">{error}</div>
        <button
          onClick={manualRefresh}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  const statusCounts = getStatusCounts();

  return (
    <>
      <div className="calendar-container p-0 mt-3 " style={{ borderBottom: `5px solid ${colorData?.secondary}`, paddingBottom: "60px", paddingTop: "100px" }}>


        <FullCalendar
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView="Weekly"
          selectable={true}
          select={handleSelectRange}
          views={{
            Weekly: {
              type: 'timeGrid',
              duration: { days: 7 },
              visibleRange: (currentDate) => {
                return {
                  start: currentDate,
                  end: new Date(currentDate.getTime() + 7 * 24 * 60 * 60 * 1000)
                };
              }
            }
          }}
          nowIndicator={true}
          validRange={{
            start: new Date().toISOString().split('T')[0]
          }}
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'Weekly,timeGridDay'
          }}
          events={events}
          slotMinTime="08:00:00"
          slotMaxTime="20:00:00"
          slotDuration="00:30:00"
          allDaySlot={false}
          height="auto"
          editable={true}
          selectMirror={true}
          dayMaxEvents={true}
          weekends={true}
          dateClick={handleDateClick}
          eventClick={handleEventClick}
          eventDrop={handleEventDrop}
          eventResize={handleEventResize}
        />

        <div className="mt-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div className="text-sm text-gray-600 poppin">
            <span className='poppin mb-3'>Showing {events.length} upcoming booking{events.length !== 1 ? 's' : ''}</span>
            {events.length > 0 && (
              <span className="ml-2 hidden sm:inline">
                • Next booking: {new Date(events[0]?.start).toLocaleDateString()}
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-2 text-xs text-gray-500 mt-3">
            <span className={`px-2 py-2 rounded bg-primary poppin text-white ${statusCounts.scheduled > 0 ? 'bg-blue-100 text-blue-800' : 'bg-gray-100'}`}>
              {statusCounts.scheduled} scheduled
            </span>
            <span className={`px-2 py-2 rounded bg-success poppin text-white ms-2  ${statusCounts.confirmed > 0 ? 'bg-green-100 text-green-800' : 'bg-gray-100'}`}>
              {statusCounts.confirmed} confirmed
            </span>
            <span className={`px-2 py-2 rounded bg-danger poppin text-white ms-2  ${statusCounts.cancelled > 0 ? 'bg-red-100 text-red-800' : 'bg-gray-100'}`}>
              {statusCounts.cancelled} cancelled
            </span>
          </div>
        </div>
      </div>

      {/* Selection Dialog */}
      <SelectionDialog
        open={showSelectionDialog}
        onClose={() => {
          setShowSelectionDialog(false);
          handleCancelSelection();
        }}
        title="Select Action"
        message={`Selected time: ${selectedRange ? formatTimeForDisplay(selectedRange.start, selectedRange.end) : ''}`}
        options={[
          {
            label: "Create New Booking",
            variant: "success",
            onClick: () => {
              handleCreateBooking();
            }
          },
          {
            label: "Block Time as Unavailable",
            variant: "danger",
            onClick: () => {
              handleBlockBooking();
            }
          }
        ]}
      />

      {/* Booking Popup */}
      <BookingPopup
        isOpen={isPopupOpen}
        onClose={closePopup}
        bookingDetails={selectedBooking}
        onStatusUpdate={handleBookingStatusUpdate}
        clientId={clientId}
        calendarId={calendarId}
        onBookingDeleted={fetchBookings}
        onBookingCreated={(bookingId) => {
          fetchBookings();
        }}
      />

      {/* Confirmation Dialogs */}
      <ConfirmationDialog
        isOpen={showRescheduleConfirm}
        title="Reschedule Booking"
        message="Are you sure you want to reschedule this booking?"
        onConfirm={handleRescheduleConfirm}
        onCancel={handleRescheduleCancel}
      />

      <ConfirmationDialog
        isOpen={showDurationConfirm}
        title="Change Duration"
        message="Are you sure you want to change the duration of this booking?"
        onConfirm={handleDurationConfirm}
        onCancel={handleDurationCancel}
      />
    </>
  );
};

export default BusinessCalendar;